import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Box,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Stack,
  Tooltip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Snackbar,
  Backdrop,
  SvgIcon,
  useTheme,
  alpha,
  Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import axios from 'axios';
import PaymentIcon from '@mui/icons-material/Payment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChatIcon from '@mui/icons-material/Chat';
import EmailIcon from '@mui/icons-material/Email';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ReplyIcon from '@mui/icons-material/Reply';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import RapydPaymentForm from '../components/RapydPaymentForm';
import RapydCheckoutForm from '../components/RapydCheckoutForm';
import PaymentOptionForm from '../components/PaymentOptionForm';
import InstallationDialog from '../components/dashboard/InstallationDialog';
import { useInstallation } from '../hooks/useInstallation';
import ChatbotDialog from '../components/dashboard/ChatbotDialog';
import DeleteConfirmationDialog from '../components/dashboard/DeleteConfirmationDialog';
import SubscriptionDetails from '../components/dashboard/SubscriptionDetails';
import SubscriptionPlanDialog from '../components/dashboard/SubscriptionPlanDialog';
import CancelSubscriptionDialog from '../components/dashboard/CancelSubscriptionDialog';
import MessagesTable from '../components/dashboard/MessagesTable';
import ChatbotsList from '../components/dashboard/ChatbotsList';

interface Chatbot {
  _id: string;
  name: string;
  welcomeMessage: string;
  primaryColor: string;
  position: 'bottom-right' | 'bottom-left';
  iconType?: 'default' | 'agent' | 'emoji-smile' | 'emoji-wave' | 'question' | 'support' | 'custom';
  customIconUrl?: string;
  profilePictureUrl?: string;
  widgetShape?: 'circle' | 'bubble';
  fontFamily?: 'Roboto' | 'Open Sans' | 'Lato' | 'Montserrat' | 'Playfair Display' | 'Poppins' | 'Inter';
  settings: {
    allowFileUpload: boolean;
    businessHours: {
      enabled: boolean;
      start: string;
      end: string;
      timezone: string;
      allowLeaveMessageOutsideHours?: boolean;
    };
    defaultQuickReplies: {
      enabled: boolean;
      replies: string[];
      maxDisplay?: number;
    };
    allowLeaveMessage: boolean;
    soundSettings?: {
      enabled: boolean;
      messageSentSound: string;
      messageReceivedSound: string;
    };
    aiSettings: {
      enabled: boolean;
      customInstructions: string;
      model: string;
      temperature: number;
      maxTokens: number;
    };
  };
  keywordResponses?: KeywordResponse[];
  trialStatus: {
    isInTrial: boolean;
    trialEndDate: string;
    trialStartDate: string;
    firstApiCallDate: string | null;
  };
}

interface KeywordResponse {
  _id: string;
  keywords: string[];
  response: string;
  priority: number;
  isActive: boolean;
  responseVariations?: string[];
  variables?: {
    name: string;
    defaultValue: string;
  }[];
  followUpQuestions?: {
    question: string;
    nextResponse?: string;
  }[];
}

interface ChatbotFormData {
  name: string;
  welcomeMessage: string;
  primaryColor: string;
  position: 'bottom-right' | 'bottom-left';
  iconType?: 'default' | 'agent' | 'emoji-smile' | 'emoji-wave' | 'question' | 'support' | 'custom';
  customIconUrl?: string;
  profilePictureUrl?: string;
  widgetShape?: 'circle' | 'bubble';
  fontFamily?: 'Roboto' | 'Open Sans' | 'Lato' | 'Montserrat' | 'Playfair Display' | 'Poppins' | 'Inter';
  trialStatus: {
    isInTrial: boolean;
    trialEndDate: string;
    trialStartDate: string;
    firstApiCallDate: string | null;
  };
  settings: {
    allowFileUpload: boolean;
    businessHours: {
      enabled: boolean;
      start: string;
      end: string;
      timezone: string;
      allowLeaveMessageOutsideHours?: boolean;
    };
    defaultQuickReplies: {
      enabled: boolean;
      replies: string[];
      maxDisplay?: number;
    };
    allowLeaveMessage: boolean;
    soundSettings: {
      enabled: boolean;
      messageSentSound: string;
      messageReceivedSound: string;
    };
    aiSettings: {
      enabled: boolean;
      customInstructions: string;
      model: string;
      temperature: number;
      maxTokens: number;
    };
  };
}

interface ResponseFormData {
  keywords: string[];
  response: string;
  responseVariations: string[];
  variables: {
  name: string;
    defaultValue: string;
  }[];
  priority: number;
  isActive: boolean;
  followUpQuestions: {
    question: string;
    nextResponse?: string;
  }[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface User {
  _id: string;
  email: string;
  role: 'admin' | 'business';
  businessName: string;
  website: string;
  subscription: {
    plan: 'none' | 'basic' | 'enterprise';
    status: 'active' | 'inactive' | 'cancelled';
    startDate: string;
    endDate: string;
    maxChatbots: number;
    price: number;
    rapydSubscriptionId?: string;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    autoRenew?: boolean;
    features: {
      prioritySupport: boolean;
    };
  };
}

interface ContactMessage {
  _id: string;
  name: string;
  email: string;
  message: string;
  status: 'new' | 'read' | 'replied';
  createdAt: string;
  chatbotId: string;
}

// Add new interface for payment data
interface PaymentFormData {
  cardNumber: string;
  cardholderName: string;
  expirationMonth: string;
  expirationYear: string;
  cvv: string;
}

// Add new interface for payment methods
interface PaymentMethod {
  type: string;
  name: string;
  image: string;
  category: string;
}

// Add this type declaration for settings object with index signature
type ChatbotSettings = {
  allowFileUpload: boolean;
  businessHours: {
    enabled: boolean;
    start: string;
    end: string;
    timezone: string;
    allowLeaveMessageOutsideHours?: boolean;
  };
  defaultQuickReplies: {
    enabled: boolean;
    replies: string[];
    maxDisplay?: number;
  };
  allowLeaveMessage: boolean;
  soundSettings: {
    enabled: boolean;
    messageSentSound: string;
    messageReceivedSound: string;
  };
  aiSettings: {
    enabled: boolean;
    customInstructions: string;
    model: string;
    temperature: number;
    maxTokens: number;
  };
  [key: string]: any; // Add index signature to allow string indexing
};

// Add a function to play sounds preview
const playSound = (soundName: string) => {
  if (soundName === 'none') return;
  
  console.log(`Attempting to play sound: ${soundName}`);
  // Use API_URL to correctly reference server path
  const audio = new Audio(`${API_URL}/sounds/${soundName}.mp3`);
  audio.volume = 1.0; // Ensure volume is set to maximum
  
  // Add event listeners for debugging
  audio.addEventListener('canplaythrough', () => {
    console.log(`Sound ready to play: ${soundName}`);
    audio.play()
      .then(() => console.log(`Playing sound: ${soundName}`))
      .catch(error => console.error('Error playing sound:', error));
  });
  
  audio.addEventListener('error', (e) => {
    console.error(`Audio error (${soundName}):`, e);
  });
  
  // Force load the audio
  audio.load();
};

const BusinessDashboard: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [chatbots, setChatbots] = useState<Chatbot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedChatbot, setSelectedChatbot] = useState<Chatbot | null>(null);
  const [formData, setFormData] = useState<ChatbotFormData>({
    name: '',
    welcomeMessage: '',
    primaryColor: '#1976d2',
    position: 'bottom-right',
    iconType: 'default',
    customIconUrl: '',
    profilePictureUrl: '',
    widgetShape: 'circle',
    fontFamily: 'Roboto',
    trialStatus: {
      isInTrial: false,
      trialStartDate: '',
      trialEndDate: '',
      firstApiCallDate: ''
    },
    settings: {
      allowFileUpload: true,
      businessHours: {
        enabled: true,
        start: '09:00',
        end: '17:00',
        timezone: 'UTC',
        allowLeaveMessageOutsideHours: true
      },
      defaultQuickReplies: {
        enabled: true,
        replies: [],
        maxDisplay: 4
      },
      allowLeaveMessage: false,
      soundSettings: {
        enabled: true,
        messageSentSound: 'pop',
        messageReceivedSound: 'notification'
      },
      aiSettings: {
        enabled: false,
        customInstructions: '',
        model: 'gpt-3.5-turbo',
        temperature: 0.5,
        maxTokens: 2000
      }
    }
  });
  const [responseFormData, setResponseFormData] = useState<ResponseFormData>({
    keywords: [],
    response: '',
    responseVariations: [],
    variables: [],
    priority: 1,
    isActive: true,
    followUpQuestions: []
  });
  const [selectedResponse, setSelectedResponse] = useState<KeywordResponse | null>(null);
  const [openResponseDialog, setOpenResponseDialog] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');

  // Add new state for keyword responses
  const [keywordResponses, setKeywordResponses] = useState<KeywordResponse[]>([]);
  const [openKeywordResponseDialog, setOpenKeywordResponseDialog] = useState(false);
  const [keywordResponseFormData, setKeywordResponseFormData] = useState({
    keywords: [],
    response: '',
    priority: 0,
    isActive: true
  });

  // Add this to your state declarations
  const [availableResponses, setAvailableResponses] = useState<KeywordResponse[]>([]);

  // Add this to your state declarations
  const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'enterprise' | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);

  // Add new state for tab management
  const [tabValue, setTabValue] = useState(0);

  // Add new state for messages
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [messagesError, setMessagesError] = useState<string | null>(null);

  // Add polling interval state and cleanup
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Add state for delete confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [chatbotToDelete, setChatbotToDelete] = useState<Chatbot | null>(null);
  const [confirmationName, setConfirmationName] = useState('');

  // Add new state for payment form
  const [paymentFormData, setPaymentFormData] = useState<PaymentFormData>({
    cardNumber: '',
    cardholderName: '',
    expirationMonth: '',
    expirationYear: '',
    cvv: ''
  });
  
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);

  // Add state for 3DS verification
  const [isRedirecting3DS, setIsRedirecting3DS] = useState(false);
  const [verificationUrl, setVerificationUrl] = useState<string | null>(null);
  const [pendingPaymentId, setPendingPaymentId] = useState<string | null>(null);

  // Add state for snackbar notifications if not already present
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [isLoading, setIsLoading] = useState(false);

  // Add this state variable with the other state variables at the top of the component
  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  // Add new state for checkout dialog
  const [openCheckoutDialog, setOpenCheckoutDialog] = useState(false);

  // Helper function to validate and format response text
  const formatResponseText = (text: string) => {
    // List of valid system variables
    const systemVariables = [
      'business_name',
      'open_status',
      'start_time',
      'end_time',
      'current_time',
      'current_date'
    ];

    // Replace any malformed variable references
    let formattedText = text;
    
    // First, normalize any existing variables that have curly braces
    formattedText = formattedText.replace(/{(\s*[\w_]+\s*)}/g, (match, name) => {
      const trimmedName = name.trim();
      if (systemVariables.includes(trimmedName)) {
        return `{${trimmedName}}`;
      }
      return match;
    });

    // Then, add curly braces to any system variables that don't have them
    systemVariables.forEach(variable => {
      const regex = new RegExp(`\\b${variable}\\b(?![}])`, 'g');
      formattedText = formattedText.replace(regex, `{${variable}}`);
    });

    return formattedText;
  };

  const handleDeleteResponse = async (id: string) => {
    try {
      await axios.delete(`${API_URL}/api/keyword-responses/${id}`, {
        withCredentials: true
      });
      
      const updatedChatbotResponse = await axios.get(
        `${API_URL}/api/business/chatbots`,
        { withCredentials: true }
      );
      
      const updatedChatbots = updatedChatbotResponse.data;
      setChatbots(updatedChatbots);
      
      if (selectedChatbot) {
        const updatedSelectedChatbot = updatedChatbots.find(
          (c: Chatbot) => c._id === selectedChatbot._id
        );
        if (updatedSelectedChatbot) {
          setSelectedChatbot(updatedSelectedChatbot);
        }
      }
    } catch (err) {
      console.error('Error deleting response:', err);
      setError('Failed to delete response');
    }
  };

  // Check if this is a return from payment with URL parameters
  const checkForPaymentReturn = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionTimestamp = urlParams.get('session');
    const userId = urlParams.get('uid');
    const planType = urlParams.get('plan');
    const devMode = urlParams.get('devMode') === 'true';
    
    if (paymentStatus === 'success') {
      // Log the payment return
      console.log('Detected return from payment:', {
        paymentStatus,
        sessionTimestamp,
        userId,
        planType,
        devMode
      });
      
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Show loading indicator
      setIsLoading(true);
      
      try {
        // Verify the checkout session directly
        const verifyResponse = await axios.post(
          `${API_URL}/api/payments/verify-checkout-session`,
          { 
            sessionTimestamp,
            userId,
            planType,
            devMode
          },
          { withCredentials: true }
        );
        
        if (verifyResponse.data.success) {
          // Reload user profile to get updated subscription
          await fetchUserProfile();
          
          // Show success message
          const message = devMode 
            ? 'Development mode: Subscription activated successfully!'
            : 'Payment successful! Your subscription is now active.';
          
          setSnackbarMessage(message);
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          
          // Close any open dialogs
          setOpenCheckoutDialog(false);
          setOpenPaymentDialog(false);
        } else {
          console.error('Payment verification failed:', verifyResponse.data);
          setSnackbarMessage('Payment verification failed. Please contact support.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        
        // Try a fallback fetch of user data
        try {
          await fetchUserProfile();
          setSnackbarMessage('Your account has been updated. Please check your subscription status.');
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
        } catch (fetchError) {
          console.error('Error fetching user profile:', fetchError);
          setSnackbarMessage('Error updating your data. Please refresh the page.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Regular useEffect for initial data loading
  useEffect(() => {
    fetchUserProfile();
    fetchChatbots();
    fetchMessages();
    fetchAvailableResponses();
    fetchPaymentMethods();
    
    // Check for payment return parameters when component mounts
    checkForPaymentReturn();
  }, []);

  useEffect(() => {
    // Start polling when tab is on messages
    if (tabValue === 2) {
      const interval = setInterval(fetchMessages, 5000); // Poll every 5 seconds
      setPollingInterval(interval);
    }

    // Cleanup polling when component unmounts or tab changes
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    };
  }, [tabValue]); // Add tabValue as dependency

  // Update useEffect to fetch responses when dialog opens
  useEffect(() => {
    if (openResponseDialog) {
      fetchAvailableResponses();
    }
  }, [openResponseDialog]);

  const fetchChatbots = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/business/chatbots`, {
        withCredentials: true
      });
      setChatbots(response.data);
    } catch (err) {
      setError('Failed to fetch chatbots');
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/business/profile`, {
        withCredentials: true
      });
      setUserProfile(response.data);
    } catch (err) {
      setError('Failed to fetch user profile');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/business/messages`, {
        withCredentials: true
      });
      setMessages(response.data);
    } catch (error) {
      setMessagesError('Failed to fetch messages');
      console.error('Error fetching messages:', error);
    }
  };

  const fetchAvailableResponses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/keyword-responses`, {
        withCredentials: true
      });
      setAvailableResponses(response.data);
    } catch (error) {
      console.error('Error fetching available responses:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payments/methods`, {
        withCredentials: true
      });
      if (response.data && response.data.data) {
        // Filter to show only card payment methods
        const cardMethods = response.data.data.filter(
          (method: any) => method.category === 'card'
        );
        setPaymentMethods(cardMethods);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const handleOpenDialog = (chatbot?: Chatbot) => {
    if (chatbot) {
      setSelectedChatbot(chatbot);
      
      // Make a copy of the chatbot data
      const chatbotData = {
        name: chatbot.name,
        welcomeMessage: chatbot.welcomeMessage,
        primaryColor: chatbot.primaryColor,
        position: chatbot.position,
        iconType: chatbot.iconType || 'default',
        customIconUrl: chatbot.customIconUrl || '',
        profilePictureUrl: chatbot.profilePictureUrl || '',
        widgetShape: chatbot.widgetShape || 'circle',
        fontFamily: chatbot.fontFamily || 'Roboto',
        trialStatus: chatbot.trialStatus,
        settings: {
          allowFileUpload: chatbot.settings.allowFileUpload,
          businessHours: {
            enabled: chatbot.settings.businessHours.enabled,
            start: chatbot.settings.businessHours.start,
            end: chatbot.settings.businessHours.end,
            timezone: chatbot.settings.businessHours.timezone,
            allowLeaveMessageOutsideHours: chatbot.settings.businessHours.allowLeaveMessageOutsideHours !== false
          },
          defaultQuickReplies: chatbot.settings.defaultQuickReplies || {
            enabled: true,
            replies: [],
            maxDisplay: 4
          },
          allowLeaveMessage: chatbot.settings.allowLeaveMessage,
          soundSettings: chatbot.settings.soundSettings || {
            enabled: true,
            messageSentSound: 'pop',
            messageReceivedSound: 'notification'
          },
          aiSettings: {
            enabled: chatbot.settings.aiSettings?.enabled || false,
            customInstructions: chatbot.settings.aiSettings?.customInstructions || '',
            model: 'gpt-3.5-turbo', // Always use GPT-3.5 Turbo
            temperature: chatbot.settings.aiSettings?.temperature || 0.5,
            maxTokens: 2000 // Fixed default value
          }
        }
      };

      // If user is not on enterprise plan or not an admin, disable AI settings
      if (userProfile && userProfile.subscription.plan !== 'enterprise' && userProfile.role !== 'admin') {
        chatbotData.settings.aiSettings.enabled = false;
      }

      setFormData(chatbotData);
    } else {
      setSelectedChatbot(null);
      setFormData({
        name: '',
        welcomeMessage: '',
        primaryColor: '#1976d2',
        position: 'bottom-right',
        iconType: 'default',
        customIconUrl: '',
        profilePictureUrl: '',
        widgetShape: 'circle',
        fontFamily: 'Roboto',
        trialStatus: {
          isInTrial: false,
          trialStartDate: '',
          trialEndDate: '',
          firstApiCallDate: ''
        },
        settings: {
          allowFileUpload: true,
          businessHours: {
            enabled: true,
            start: '09:00',
            end: '17:00',
            timezone: 'UTC',
            allowLeaveMessageOutsideHours: true
          },
          defaultQuickReplies: {
            enabled: true,
            replies: [],
            maxDisplay: 4
          },
          allowLeaveMessage: false,
          soundSettings: {
            enabled: true,
            messageSentSound: 'pop',
            messageReceivedSound: 'notification'
          },
          aiSettings: {
            enabled: false,
            customInstructions: '',
            model: 'gpt-3.5-turbo', // Always use GPT-3.5 Turbo
            temperature: 0.5,
            maxTokens: 2000 // Fixed default value
          }
        }
      });
    }
    setOpenDialog(true);
  };

  const handleOpenResponseDialog = (response?: KeywordResponse) => {
    if (response) {
      setSelectedResponse(response);
      setResponseFormData({
        keywords: response.keywords,
        response: response.response,
        responseVariations: response.responseVariations || [],
        variables: response.variables || [],
        priority: response.priority,
        isActive: response.isActive,
        followUpQuestions: response.followUpQuestions || []
      });
      setKeywordInput(response.keywords.join(', '));
    } else {
      setSelectedResponse(null);
      setResponseFormData({
        keywords: [],
        response: '',
        responseVariations: [],
        variables: [],
        priority: 1,
        isActive: true,
        followUpQuestions: []
      });
      setKeywordInput('');
    }
    setOpenResponseDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedChatbot(null);
  };

  const handleCloseResponseDialog = () => {
    setOpenResponseDialog(false);
    setSelectedResponse(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePriorityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      setResponseFormData(prev => ({
        ...prev,
        priority: value
      }));
    }
  };

  const handleKeywordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeywordInput(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Create a copy of the form data to ensure we don't unexpectedly modify state
      const submissionData = { 
        ...formData,
        settings: {
          ...formData.settings,
          aiSettings: {
            ...formData.settings.aiSettings,
            model: 'gpt-3.5-turbo', // Always ensure GPT-3.5 Turbo is used
            maxTokens: 2000 // Fixed token limit
          }
        }
      };

      // Force disable AI for non-enterprise users
      if (userProfile && userProfile.subscription.plan !== 'enterprise' && userProfile.role !== 'admin') {
        submissionData.settings.aiSettings.enabled = false;
      }

      if (selectedChatbot) {
        // Update existing chatbot
        await axios.put(
          `${API_URL}/api/business/chatbot/${selectedChatbot._id}`,
          submissionData,
          { withCredentials: true }
        );
        setChatbots(chatbots.map(c =>
          c._id === selectedChatbot._id
            ? { ...c, ...submissionData }
            : c
        ));
      } else {
        // Create new chatbot
        const response = await axios.post(
          `${API_URL}/api/business/chatbot`,
          submissionData,
          { withCredentials: true }
        );
        setChatbots([...chatbots, response.data]);
      }
      handleCloseDialog();
    } catch (error: any) {
      console.error('Error saving:', error);
      if (error.response?.status === 403) {
        setError(error.response.data.message || 'You do not have permission to create more chatbots. Please check your subscription status.');
      } else {
        setError(error instanceof Error ? error.message : 'Failed to save');
      }
    }
  };

  const handleResponseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const keywords = keywordInput.split(',').map(k => k.trim()).filter(k => k);
      
      const formattedResponse = formatResponseText(responseFormData.response);
      const formattedVariations = responseFormData.responseVariations.map(formatResponseText);
      
      const responseData = {
        ...responseFormData,
        response: formattedResponse,
        responseVariations: formattedVariations.filter(v => v.trim()),
        keywords,
        chatbotId: selectedChatbot?._id,
        variables: responseFormData.variables.filter(v => v.name),
        followUpQuestions: responseFormData.followUpQuestions.filter(fq => fq.question.trim())
      };

      if (selectedResponse) {
        await axios.put(
          `${API_URL}/api/keyword-responses/${selectedResponse._id}`,
          responseData,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_URL}/api/keyword-responses`,
          responseData,
          { withCredentials: true }
        );
      }
      handleCloseResponseDialog();
      
      const updatedChatbotResponse = await axios.get(
        `${API_URL}/api/business/chatbots`,
        { withCredentials: true }
      );
      
      const updatedChatbots = updatedChatbotResponse.data;
      setChatbots(updatedChatbots);
      
      const updatedSelectedChatbot = updatedChatbots.find(
        (c: Chatbot) => c._id === selectedChatbot?._id
      );
      if (updatedSelectedChatbot) {
        setSelectedChatbot(updatedSelectedChatbot);
      }
    } catch (err) {
      console.error('Error saving response:', err);
      setError(err instanceof Error ? err.message : 'Failed to save response');
    }
  };

  const handleDelete = (chatbot: Chatbot) => {
    setChatbotToDelete(chatbot);
    setOpenDeleteDialog(true);
    setConfirmationName('');
  };

  const handleConfirmDelete = async () => {
    if (!chatbotToDelete || confirmationName !== chatbotToDelete.name) {
      setError('Please type the exact chatbot name to confirm deletion');
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/business/chatbot/${chatbotToDelete._id}`, {
        withCredentials: true
      });
      setChatbots(chatbots.filter(c => c._id !== chatbotToDelete._id));
      setOpenDeleteDialog(false);
      setChatbotToDelete(null);
      setConfirmationName('');
    } catch (err) {
      console.error('Error deleting:', err);
      setError('Failed to delete chatbot');
    }
  };

  const handleKeywordResponseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedChatbot) return;

    try {
      const response = await axios.post(
        `${API_URL}/api/keyword-responses`,
        {
          ...keywordResponseFormData,
          chatbotId: selectedChatbot._id
        },
        { withCredentials: true }
      );
      setKeywordResponses([...keywordResponses, response.data]);
      setOpenKeywordResponseDialog(false);
      setKeywordResponseFormData({
        keywords: [],
        response: '',
        priority: 0,
        isActive: true
      });
    } catch (err) {
      setError('Failed to create keyword response');
    }
  };

  const handleEditResponse = (response: KeywordResponse) => {
    setSelectedResponse(response);
    setKeywordInput(response.keywords.join(', '));
    setResponseFormData({
      keywords: response.keywords,
      response: response.response,
      responseVariations: response.responseVariations || [],
      variables: response.variables || [],
      priority: response.priority,
      isActive: response.isActive,
      followUpQuestions: response.followUpQuestions || []
    });
    setOpenResponseDialog(true);
  };

  // Modify handleSubscribe to open checkout dialog instead of payment dialog
  const handleSubscribe = async (plan: 'basic' | 'enterprise') => {
    setSelectedPlan(plan);
    
    // In development, simulate successful subscription without payment flow
    if (process.env.NODE_ENV === 'development') {
      // Simulate subscription success
      setSnackbarMessage(`Development mode: ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan activated instantly.`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Update user profile with simulated subscription data
      if (userProfile) {
        const maxChatbots = plan === 'basic' ? 1 : 3;
        setUserProfile({
          ...userProfile,
          subscription: {
            ...userProfile.subscription,
            plan: plan,
            status: 'active',
            startDate: new Date().toISOString(),
            endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
            autoRenew: true,
            maxChatbots: maxChatbots
          }
        });
      }
      
      // Close subscription dialog
      setOpenSubscriptionDialog(false);
    } else {
      // Normal flow for production
      setOpenCheckoutDialog(true);
      setOpenSubscriptionDialog(false);
    }
  };

  // Add function to handle 3DS verification completion
  const handle3DSCompletion = useCallback(async () => {
    if (!pendingPaymentId) return;
    
    try {
      setIsProcessingPayment(true);
      
      const response = await axios.post(
        `${API_URL}/api/payments/verify-3ds`,
        { paymentId: pendingPaymentId },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setPaymentSuccess(true);
        await fetchUserProfile();
        
        // Reset 3DS state
        setPendingPaymentId(null);
        setVerificationUrl(null);
        
        // Close payment dialog after successful payment
        setTimeout(() => {
          setOpenPaymentDialog(false);
          setPaymentSuccess(false);
        }, 2000);
      } else {
        setPaymentError('3DS verification failed. Please try again.');
      }
    } catch (error: any) {
      console.error('3DS verification error:', error);
      setPaymentError(
        error.response?.data?.message || 
        '3DS verification failed. Please try again.'
      );
    } finally {
      setIsProcessingPayment(false);
      setIsRedirecting3DS(false);
    }
  }, [pendingPaymentId, API_URL]);

  // Add effect to check for 3DS verification completion
  useEffect(() => {
    // Function to check if this is a return from 3DS verification
    const checkFor3DSReturn = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const status = urlParams.get('payment_status');
      
      if (status && pendingPaymentId) {
        // Clear URL parameters without page refresh
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Verify the payment
        handle3DSCompletion();
      }
    };
    
    checkFor3DSReturn();
  }, [pendingPaymentId, handle3DSCompletion]);

  // Update handleProcessPayment function
  const handleProcessPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedPlan) {
      setPaymentError('Please select a plan first');
      return;
    }

    setIsProcessingPayment(true);
    setPaymentError(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/process`,
        {
          cardDetails: paymentFormData,
          plan: selectedPlan
        },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        // Check if 3DS verification is required
        if (response.data.requires3DS && response.data.redirectUrl) {
          setVerificationUrl(response.data.redirectUrl);
          setPendingPaymentId(response.data.paymentId);
          setIsRedirecting3DS(true);
          
          // Open the 3DS verification in a new window
          window.open(response.data.redirectUrl, '_self');
          return;
        }
        
        // Standard success flow
        setPaymentSuccess(true);
        // Fetch updated user profile with new subscription status
        await fetchUserProfile();
        // Close payment dialog after successful payment
        setTimeout(() => {
          setOpenPaymentDialog(false);
          setPaymentSuccess(false);
        }, 2000);
      }
    } catch (error: any) {
      console.error('Payment processing error:', error);
      setPaymentError(
        error.response?.data?.message || 
        'Payment processing failed. Please try again.'
      );
    } finally {
      if (!isRedirecting3DS) {
        setIsProcessingPayment(false);
      }
    }
  };

  // Add function to handle payment form input changes
  const handlePaymentInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPaymentFormData({
      ...paymentFormData,
      [name]: value
    });
  };

  // Add function to handle closing payment dialog
  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    setPaymentError(null);
    setPaymentSuccess(false);
    setPaymentFormData({
      cardNumber: '',
      cardholderName: '',
      expirationMonth: '',
      expirationYear: '',
      cvv: ''
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Clear existing polling interval
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }

    // Start polling if switching to messages tab
    if (newValue === 2) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 5000);
      setPollingInterval(interval);
    }
  };

  const handleUpdateMessageStatus = async (messageId: string, newStatus: 'read' | 'replied') => {
    try {
      await axios.patch(
        `${API_URL}/api/contact/${messageId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      
      // Update the local state to reflect the change
      setMessages(messages.map(msg => 
        msg._id === messageId ? { ...msg, status: newStatus } : msg
      ));
    } catch (error) {
      console.error('Error updating message status:', error);
      setError('Failed to update message status');
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/contact/${messageId}`);
      setMessages(messages.filter(msg => msg._id !== messageId));
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message. Please try again.');
    }
  };

  // Now using the useInstallation hook instead of implementing these handlers locally

  // Add this function to calculate unread messages
  const getUnreadMessagesCount = () => {
    return messages.filter(msg => msg.status === 'new').length;
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      setError('Failed to logout');
    }
  };

  // Fix setLoading to setIsLoading
  const handleCancelSubscription = async () => {
    setIsLoading(true);
    
    try {
      // In development, simulate successful cancellation without API call
      if (process.env.NODE_ENV === 'development') {
        setSnackbarMessage('Development mode: Subscription cancelled immediately.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        // Update user profile with simulated data for dev environment
        if (userProfile) {
          setUserProfile({
            ...userProfile,
            subscription: {
              ...userProfile.subscription,
              autoRenew: false,
              status: 'cancelled'
            }
          });
        }
      } else {
        // Production logic remains unchanged
        const response = await axios.post(`${API_URL}/api/payments/cancel-subscription`, {}, {
          withCredentials: true
        });
        
        if (response.data.success) {
          setSnackbarMessage('Subscription will be cancelled at the end of the billing period.');
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
          
          // Refresh user data to get updated subscription information
          await fetchUserProfile();
        } else {
          setSnackbarMessage(`Failed to cancel subscription: ${response.data.message || 'Unknown error'}`);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      setSnackbarMessage('Failed to cancel subscription. Please try again later.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
      setOpenCancelDialog(false);
    }
  };

  // Add a handler for payment success
  const handlePaymentSuccess = (subscriptionData: any) => {
    fetchUserProfile();
  };

  // Add these handler functions
  const handleOpenCancelDialog = () => {
    setOpenCancelDialog(true);
  };

  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
  };

  // Function to play sound previews
  const playSound = (soundName: string) => {
    if (soundName === 'none') return;
    
    console.log(`Attempting to play sound: ${soundName}`);
    // Use API_URL to correctly reference server path
    const audio = new Audio(`${API_URL}/sounds/${soundName}.mp3`);
    audio.volume = 1.0; // Ensure volume is set to maximum
    
    // Add event listeners for debugging
    audio.addEventListener('canplaythrough', () => {
      console.log(`Sound ready to play: ${soundName}`);
      audio.play()
        .then(() => console.log(`Playing sound: ${soundName}`))
        .catch(error => console.error('Error playing sound:', error));
    });
    
    audio.addEventListener('error', (e) => {
      console.error(`Audio error (${soundName}):`, e);
    });
    
    // Force load the audio
    audio.load();
  };

  // Add a function to handle nested settings changes
  const handleSettingsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, path: string) => {
    const { value, type, checked } = e.target as HTMLInputElement; // Cast to HTMLInputElement to get checked property
    const pathParts = path.split('.');
    
    setFormData(prev => {
      // Create a deep copy of the settings to avoid mutating state directly
      const updatedSettings = { ...prev.settings } as ChatbotSettings;
      
      if (pathParts.length === 2) {
        const [section, field] = pathParts;
        if (!updatedSettings[section]) {
          updatedSettings[section] = {};
        }
        
        // Handle nested objects like aiSettings, soundSettings, etc.
        updatedSettings[section] = {
          ...updatedSettings[section],
          [field]: type === 'checkbox' ? checked : value
        };
      } else if (pathParts.length === 3) {
        const [section, subsection, field] = pathParts;
        if (!updatedSettings[section]) {
          updatedSettings[section] = {};
        }
        if (!updatedSettings[section][subsection]) {
          updatedSettings[section][subsection] = {};
        }
        
        // Handle deeper nested objects like businessHours.enabled
        updatedSettings[section] = {
          ...updatedSettings[section],
          [subsection]: {
            ...updatedSettings[section][subsection],
            [field]: type === 'checkbox' ? checked : value
          }
        };
      }
      
      return {
        ...prev,
        settings: updatedSettings
      };
    });
  };

  // Get installation related functionality from custom hook
  const {
    openInstallDialog,
    selectedChatbotForInstall,
    copySuccess,
    handleOpenInstallDialog,
    handleCloseInstallDialog,
    handleCopyCode,
    getInstallationCode
  } = useInstallation();

  return (
    <Box sx={{ 
      width: '100%', 
      minHeight: '100vh',
      bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#f8f9fa',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header Section */}
      <Header />

      {/* Tabs Section */}
      <Box sx={{ 
        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white', 
        borderBottom: '1px solid',
        borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : '#e0e0e0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        position: 'sticky',
        top: 64, // Account for header height
        zIndex: 999,
      }}>
        <Container maxWidth="lg">
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2,
                mb: 2,
                borderRadius: 2
              }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="dashboard sections"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                minWidth: 120,
                color: '#666',
              },
              '& .Mui-selected': {
                color: '#1a237e',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1a237e',
                height: 3,
              },
            }}
          >
            <Tab 
              label="Subscription" 
              icon={<PaymentIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
            />
            <Tab 
              label="Chatbots" 
              icon={<ChatIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
            />
            <Tab 
              label="Messages" 
              icon={
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <EmailIcon sx={{ fontSize: 20 }} />
                  {getUnreadMessagesCount() > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        bgcolor: 'error.main',
                        color: 'white',
                        borderRadius: '50%',
                        width: 16,
                        height: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {getUnreadMessagesCount()}
                    </Box>
                  )}
                </Box>
              }
              iconPosition="start"
            />
          </Tabs>
        </Container>
      </Box>

      {/* Content Section */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: 4,
          flex: 1
        }}
      >
        {/* Subscription Tab */}
        <TabPanel value={tabValue} index={0}>
          {userProfile && (
            <SubscriptionDetails 
              userProfile={userProfile}
              onOpenSubscriptionDialog={() => setOpenSubscriptionDialog(true)}
              onOpenCancelDialog={handleOpenCancelDialog}
            />
          )}
        </TabPanel>

        {/* Chatbots Tab */}
        <TabPanel value={tabValue} index={1}>
          <ChatbotsList
            chatbots={chatbots}
            userProfile={userProfile}
            onEdit={handleOpenDialog}
            onDelete={handleDelete}
            onAdd={() => handleOpenDialog()}
            onInstall={handleOpenInstallDialog}
          />
        </TabPanel>

        {/* Messages Tab */}
        <TabPanel value={tabValue} index={2}>
          <Container maxWidth="lg">
            <MessagesTable
              messages={messages}
              error={messagesError}
              onUpdateStatus={handleUpdateMessageStatus}
              onDeleteMessage={handleDeleteMessage}
            />
          </Container>
        </TabPanel>
      </Container>

      {/* Subscription Dialog */}
      <SubscriptionPlanDialog
        open={openSubscriptionDialog}
        onClose={() => setOpenSubscriptionDialog(false)}
        onSubscribe={handleSubscribe}
        userHasActiveSubscription={userProfile?.subscription.status === 'active'}
        onOpenCancelDialog={handleOpenCancelDialog}
      />

      {/* Cancel Subscription Dialog */}
      <CancelSubscriptionDialog
        open={openCancelDialog}
        onClose={handleCloseCancelDialog}
        onConfirmCancel={handleCancelSubscription}
        isLoading={isLoading}
      />

      <RapydPaymentForm 
        open={openPaymentDialog}
        onClose={() => setOpenPaymentDialog(false)}
        onSuccess={handlePaymentSuccess}
        selectedPlan={selectedPlan}
        apiUrl={API_URL}
      />
      
      {/* Payment Option Form - preferred method with choice between card and PayPal */}
      <PaymentOptionForm
        open={openCheckoutDialog}
        onClose={() => setOpenCheckoutDialog(false)}
        onSuccess={handlePaymentSuccess}
        selectedPlan={selectedPlan}
        apiUrl={API_URL}
      />

      {/* Installation Dialog */}
      <InstallationDialog
        open={openInstallDialog}
        onClose={handleCloseInstallDialog}
        selectedChatbot={selectedChatbotForInstall}
        copySuccess={copySuccess}
        handleCopyCode={handleCopyCode}
      />

      {/* Chatbot Edit Dialog */}
      <ChatbotDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        selectedChatbot={selectedChatbot}
        userProfile={userProfile}
        handleInputChange={handleInputChange}
        handleOpenResponseDialog={handleOpenResponseDialog}
        handleEditResponse={handleEditResponse}
        handleDeleteResponse={handleDeleteResponse}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        chatbotToDelete={chatbotToDelete}
        confirmationName={confirmationName}
        onConfirmationNameChange={(e) => setConfirmationName(e.target.value)}
        onConfirmDelete={handleConfirmDelete}
      />
    </Box>
  );
};

export default BusinessDashboard; 